import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  MoreVertical,
  Settings,
  Sparkles,
  Trash2,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { isAnalyticsSection } from 'components/DisasterGPT/constants/opordSectionDefaults';
import { AnalyticsCategoryLayerPicker } from 'components/Reports/document/AnalyticsLayerActions';
import { isAutomatedLayerStatsSection } from 'components/DisasterGPT/AutomatedLayerStatsSectionPanel';
import {
  SitrepSectionType,
  useUpdateSitrepSectionV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { SectionLastUpdatedLabel } from 'components/Reports/section/SectionLastUpdatedLabel';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import {
  SitrepSectionBody,
  useSitrepSectionEditor,
} from 'components/Reports/section/SitrepSectionBody';
import { useDynamicSectionData } from 'components/Reports/section/useDynamicSectionData';
import {
  SitrepSubmittedDataTabs,
  useSitrepDynamicData,
} from 'components/Reports/section/SitrepDynamicDataContext';
import {
  sectionRendersSnapshotToggle,
  sectionsHaveAnalyticsGeoSnapshot,
} from 'components/Reports/section/dynamicSectionSnapshot';
import {
  countSitrepDataSources,
  resolveEffectiveDataSources,
} from './sitrepDataSourceCount';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';

type SitrepDocumentSectionProps = {
  section: SitrepSectionType;
  sitrepId: string;
  sitrep?: Pick<
    GetSitrepsResponse,
    'aor_id' | 'incident_id' | 'approval_status'
  >;
  sitrepWorkspaceId: string | null;
  sitrepTemplateId?: string | null;
  canEdit: boolean;
  showAuthoringActions: boolean;
  isLocked?: boolean;
  isGenerating?: boolean;
  childSections?: SitrepSectionType[];
  nested?: boolean;
  persistentlyVisible?: boolean;
  sectionAILocks?: Map<string, unknown>;
  parentDataSources?: unknown[];
  onUpdated: () => void;
  onOpenSettings?: (section: SitrepSectionType) => void;
  onDelete?: (section: SitrepSectionType) => void;
  onAIGenerate?: (section: SitrepSectionType) => void;
  onAIGenerateMany?: (sectionIds: string[]) => void;
  categoryAiIds?: string[];
  /** Current report: Live Data analytics can rerun their queries. */
  enableLiveAnalyticsRefresh?: boolean;
};

const outputFormatLabel = (section: SitrepSectionType): string | null => {
  const options = section.output_format_structured?.summary_options;
  if (!options?.length) return null;
  return options
    .map((o) => o.charAt(0).toUpperCase() + o.slice(1).toLowerCase())
    .join(' + ');
};

const SectionHeadingActions: React.FC<{
  isCategory: boolean;
  canAiGenerate: boolean;
  canOpenSettings: boolean;
  canDelete: boolean;
  sectionName: string;
  isGenerating: boolean;
  isLocked: boolean;
  isProcessing: boolean;
  aiLockedLabel: string;
  onAIGenerate: () => void;
  onOpenSettings: () => void;
  onDelete: () => void;
}> = ({
  isCategory,
  canAiGenerate,
  canOpenSettings,
  canDelete,
  sectionName,
  isGenerating,
  isLocked,
  isProcessing,
  aiLockedLabel,
  onAIGenerate,
  onOpenSettings,
  onDelete,
}) => {
  const actionCount =
    Number(canAiGenerate) + Number(canOpenSettings) + Number(canDelete);

  if (actionCount === 1 && canAiGenerate) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={`AI generate draft for ${sectionName}`}
        data-tour="reports-ai-generate"
        disabled={isGenerating || isLocked || isProcessing}
        title={isLocked ? aiLockedLabel : 'AI generate'}
        onClick={onAIGenerate}
      >
        <Sparkles className={isGenerating ? 'tw:animate-spin' : undefined} />
      </Button>
    );
  }

  if (actionCount === 1 && canOpenSettings) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Section settings"
        onClick={onOpenSettings}
      >
        <Settings />
      </Button>
    );
  }

  if (actionCount === 1 && canDelete) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={`Delete ${isCategory ? 'category' : 'section'} ${sectionName}`}
        className="tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 />
      </Button>
    );
  }

  if (actionCount < 2) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`${isCategory ? 'Category' : 'Section'} actions`}
            data-tour={canAiGenerate ? 'reports-ai-generate' : undefined}
          >
            <MoreVertical />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="dark tw:w-48">
        {canAiGenerate ? (
          <DropdownMenuItem
            data-tour="reports-ai-generate"
            disabled={isGenerating || isLocked || isProcessing}
            title={isLocked ? aiLockedLabel : undefined}
            onClick={onAIGenerate}
          >
            <Sparkles />
            AI generate
          </DropdownMenuItem>
        ) : null}
        {canOpenSettings ? (
          <DropdownMenuItem onClick={onOpenSettings}>
            <Settings />
            Section settings
          </DropdownMenuItem>
        ) : null}
        {canDelete ? (
          <DropdownMenuItem variant="destructive" onClick={onDelete}>
            <Trash2 />
            Delete {isCategory ? 'category' : 'section'}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SitrepDocumentSection: React.FC<SitrepDocumentSectionProps> = ({
  section,
  sitrepId,
  sitrep,
  sitrepWorkspaceId,
  sitrepTemplateId = null,
  canEdit,
  showAuthoringActions,
  isLocked = false,
  isGenerating = false,
  childSections = [],
  nested = false,
  persistentlyVisible = false,
  sectionAILocks,
  parentDataSources,
  onUpdated,
  onOpenSettings,
  onDelete,
  onAIGenerate,
  onAIGenerateMany,
  categoryAiIds = [],
  enableLiveAnalyticsRefresh = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const hasChildren = childSections.length > 0;
  const reportDynamic = useSitrepDynamicData();
  const showSnapshotTabs =
    Boolean(reportDynamic?.showTabs) &&
    sectionRendersSnapshotToggle(section, childSections);
  const sectionOwnsMode = Boolean(section.parent_section_id);
  const isCategory = hasChildren;
  const [dirty, setDirty] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [nameDraft, setNameDraft] = useState(section.name);

  const renameMutation = useUpdateSitrepSectionV2();
  const { editor } = useSitrepSectionEditor({
    section,
    sitrepId,
    sitrepWorkspaceId,
    canEdit,
    onUpdated,
    silent: true,
    isAiLocked: !!isLocked,
  });
  const dynamicData = useDynamicSectionData({
    section,
    sitrepId,
    approvalStatus: sitrep?.approval_status,
  });

  const { isEditing, isSaving, isProcessing, cancelEditing, save } = editor;

  const commitRename = async () => {
    setRenaming(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === section.name) {
      setNameDraft(section.name);
      return;
    }
    try {
      await renameMutation.mutateAsync({
        sitrepId,
        sectionId: section.id,
        silent: true,
        updatedFields: { name: trimmed },
      });
      onUpdated();
    } catch {
      setNameDraft(section.name);
    }
  };

  const handleSave = async () => {
    await save();
    setDirty(false);
  };

  const handleDiscard = () => {
    cancelEditing();
    setDirty(false);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isEditing || dirty) return;
    if (
      event.relatedTarget &&
      event.currentTarget.contains(event.relatedTarget as Node)
    ) {
      return;
    }
    if (!event.relatedTarget) return;
    handleDiscard();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEditing && (event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault();
      void handleSave();
    }
  };

  const isAISection = section.type === 'ai' && !isCategory;
  const isAutomated = section.type === 'automated';
  const hideBodyEditor = hasChildren;
  const HeadingTag = nested ? 'h5' : 'h4';
  const sectionDataSources = Array.isArray(section.data_sources)
    ? section.data_sources
    : Array.isArray(section.data_source)
      ? section.data_source
      : [];
  const sectionDataSourceCount = countSitrepDataSources(
    resolveEffectiveDataSources(sectionDataSources, parentDataSources)
  );

  const aiLockedLabel =
    "Someone else is generating this section right now. Wait until they're finished and you can AI generate a draft.";

  const showBody = fullscreen || !collapsed || persistentlyVisible;
  const keepAnalyticsPanel =
    isAutomatedLayerStatsSection(section) &&
    dynamicData.showToggle &&
    dynamicData.mode === 'live';
  const sectionContent = (
    <>
      {!isCategory && (
        <div
          className="tw:mb-1.5 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5"
          data-testid="reports-doc-section-chips"
        >
          {showAuthoringActions ? (
            <>
              <Badge variant="secondary">
                {isAutomated ? 'Automated' : isAISection ? 'AI' : 'Manual'}
              </Badge>
              {isAISection && (
                <Badge variant="secondary">
                  {sectionDataSourceCount} data source
                  {sectionDataSourceCount === 1 ? '' : 's'}
                </Badge>
              )}
              {outputFormatLabel(section) && (
                <Badge variant="secondary">{outputFormatLabel(section)}</Badge>
              )}
            </>
          ) : null}
          <SectionLastUpdatedLabel section={section} />
        </div>
      )}

      {showSnapshotTabs ? (
        <SitrepSubmittedDataTabs
          className="tw:mb-2"
          {...(sectionOwnsMode
            ? {
                mode: dynamicData.mode,
                onModeChange: dynamicData.setMode,
                hasGeoSnapshot: sectionsHaveAnalyticsGeoSnapshot([section]),
              }
            : {})}
        />
      ) : null}

      {hasChildren ? (
        <AnalyticsCategoryLayerPicker
          parent={section}
          childSections={childSections}
        />
      ) : null}

      {showBody ? (
        <div className="reports-doc__section-body">
          {dynamicData.isPreviewLoading && !keepAnalyticsPanel ? (
            <ReportsGeneratingPlaceholder />
          ) : (
            <SitrepSectionBody
              section={section}
              sitrepId={sitrepId}
              sitrep={sitrep}
              sitrepWorkspaceId={sitrepWorkspaceId}
              sitrepTemplateId={sitrepTemplateId}
              canEdit={canEdit}
              editor={editor}
              hideBodyEditor={hideBodyEditor}
              onContentChange={() => setDirty(true)}
              dataStringOverride={
                dynamicData.showToggle ? dynamicData.dataString : undefined
              }
              hideRun={dynamicData.hideRun}
              useLiveEnrichment={dynamicData.useLiveEnrichment}
              onRefreshAnalytics={
                keepAnalyticsPanel
                  ? () => {
                      void dynamicData.refetchPreview();
                    }
                  : undefined
              }
              refreshingAnalytics={
                keepAnalyticsPanel && dynamicData.isPreviewLoading
              }
            />
          )}

          {hasChildren ? (
            <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-2 tw:ps-7">
              {childSections.map((child) => (
                <SitrepDocumentSection
                  key={child.id}
                  section={child}
                  sitrepId={sitrepId}
                  sitrep={sitrep}
                  sitrepWorkspaceId={sitrepWorkspaceId}
                  sitrepTemplateId={sitrepTemplateId}
                  canEdit={canEdit}
                  showAuthoringActions={showAuthoringActions}
                  isLocked={!!sectionAILocks?.get(child.id)}
                  isGenerating={isGenerating}
                  nested
                  persistentlyVisible
                  parentDataSources={sectionDataSources}
                  onUpdated={onUpdated}
                  onOpenSettings={onOpenSettings}
                  onDelete={onDelete}
                  onAIGenerate={onAIGenerate}
                  enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- blur/keydown only capture events bubbling from the inner editor (blur-save, Cmd+S)
    <section
      className={cn(
        'reports-doc__section',
        isEditing && 'reports-doc__section--editing'
      )}
      data-testid="reports-doc-section"
      data-section-id={section.id}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <div
        className="reports-doc__section-heading tw:flex tw:items-center tw:gap-1.5"
        data-testid="reports-doc-section-heading"
      >
        {!persistentlyVisible ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="tw:shrink-0"
            aria-expanded={!collapsed}
            aria-label={
              collapsed
                ? `Expand ${isCategory ? 'category' : 'section'} ${section.name}`
                : `Collapse ${isCategory ? 'category' : 'section'} ${section.name}`
            }
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <ChevronRight /> : <ChevronDown />}
          </Button>
        ) : null}
        {isAISection && (
          <Sparkles
            className={cn(
              'tw:size-3.5 tw:shrink-0 tw:text-info',
              isGenerating && 'tw:animate-spin'
            )}
            aria-label="PRATUS AI generated section"
          />
        )}
        {renaming ? (
          <Input
            className={cn(
              'tw:h-7 tw:min-w-0 tw:flex-1 tw:font-semibold',
              nested ? 'tw:text-lg' : 'tw:text-xl'
            )}
            value={nameDraft}
            autoFocus
            aria-label={isCategory ? 'Category name' : 'Section name'}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => {
              void commitRename();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void commitRename();
              } else if (e.key === 'Escape') {
                setNameDraft(section.name);
                setRenaming(false);
              }
            }}
          />
        ) : (
          <HeadingTag
            className={cn(
              'tw:m-0 tw:mb-0! tw:min-w-0 tw:shrink tw:truncate tw:font-semibold tw:text-foreground',
              nested
                ? 'tw:min-h-7 tw:text-lg! tw:leading-7 tw:font-semibold!'
                : 'tw:min-h-7 tw:text-xl! tw:leading-7 tw:font-semibold!',
              showAuthoringActions &&
                'tw:cursor-text tw:rounded-md tw:px-2 tw:py-1 tw:-mx-2 tw:hover:bg-muted'
            )}
            title={showAuthoringActions ? 'Click to rename' : undefined}
            onClick={
              showAuthoringActions
                ? () => {
                    setNameDraft(section.name);
                    setRenaming(true);
                  }
                : undefined
            }
          >
            {section.name}
          </HeadingTag>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="tw:shrink-0"
          aria-label={`Full screen ${section.name}`}
          aria-pressed={fullscreen}
          onClick={() => setFullscreen(true)}
        >
          <Maximize2 />
        </Button>
        {isLocked && (
          <Badge
            variant="amber"
            title="Someone else is generating this section right now."
          >
            Locked
          </Badge>
        )}

        {isEditing && (
          <span className="tw:inline-flex tw:items-center tw:gap-1.5">
            <Badge variant={dirty && !isSaving ? 'amber' : 'outline'}>
              {isSaving ? 'Saving…' : dirty ? 'Unsaved' : 'Editing'}
            </Badge>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={handleDiscard}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="xs"
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving || !dirty}
            >
              Save
            </Button>
          </span>
        )}

        <span className="tw:ml-auto tw:inline-flex tw:items-center tw:gap-1">
          {isCategory && categoryAiIds.length > 0 && onAIGenerateMany ? (
            <Button
              type="button"
              variant="outline"
              size="xs"
              data-tour="reports-ai-generate"
              disabled={isGenerating}
              onClick={() => onAIGenerateMany(categoryAiIds)}
            >
              <Sparkles
                className={isGenerating ? 'tw:animate-spin' : undefined}
              />
              AI Generate All
            </Button>
          ) : null}
          {!isEditing && showAuthoringActions ? (
            <SectionHeadingActions
              isCategory={isCategory}
              canAiGenerate={!isCategory && isAISection && !!onAIGenerate}
              canOpenSettings={
                !isCategory && !!onOpenSettings && !isAnalyticsSection(section)
              }
              canDelete={!!onDelete}
              sectionName={section.name}
              isGenerating={isGenerating}
              isLocked={isLocked}
              isProcessing={isProcessing}
              aiLockedLabel={aiLockedLabel}
              onAIGenerate={() => onAIGenerate?.(section)}
              onOpenSettings={() => onOpenSettings?.(section)}
              onDelete={() => setShowDeleteConfirm(true)}
            />
          ) : null}
        </span>
      </div>

      {fullscreen ? (
        <FullScreenDialog
          open
          onOpenChange={setFullscreen}
          title={section.name}
          headerLeading={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFullscreen(false)}
            >
              <Minimize2 />
              Exit fullscreen
            </Button>
          }
          footer={
            isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDiscard}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={isSaving || !dirty}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : undefined
          }
        >
          {sectionContent}
        </FullScreenDialog>
      ) : (
        sectionContent
      )}

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        title={isCategory ? 'Delete Category' : 'Delete Section'}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete?.(section);
        }}
        description={
          <>
            Are you sure you want to delete &ldquo;{section.name}&rdquo;?{' '}
            {isCategory
              ? 'This removes the category and the sections inside it. This action cannot be undone.'
              : 'This removes the section and its version history. This action cannot be undone.'}
          </>
        }
      />
    </section>
  );
};

export default SitrepDocumentSection;
